import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import {
  catalogApi,
  useGetSingleCatalogQuery
} from 'Services/ProductManagement/Features/Catalog/catalogApi'
import { SELECTED_CATALOG_PARAMETER_KEY } from 'Services/ProductManagement/Features/Catalog/Hooks/useCatalogsWithParams'
import useProductGroupOptions from 'Services/ProductManagement/Features/Parameter/Hooks/useProductGroupOptions'
import { useGetCustomerGroupParameterGroupQuery } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { ProductList } from 'Services/ProductManagement/Features/Product/Components/ProductList'
import {
  useProductsWithParams,
  useSingleProduct
} from 'Services/ProductManagement/Features/Product/Hooks'
import useProductListTabs from 'Services/ProductManagement/Features/Product/Hooks/useProductListTabs'
import { ProductDeleteModes } from 'Services/ProductManagement/Features/Product/productSlice'
import TariffCatalogDrawer from 'Services/ProductManagement/Features/Tariff/Components/TariffCatalogDrawer'
import TariffCatalogProductDrawer from 'Services/ProductManagement/Features/Tariff/Components/TariffCatalogProductDrawer'
import { useSelectedTariff } from 'Services/ProductManagement/Features/Tariff/Hooks'
import { TariffCatalogTabType } from 'Services/ProductManagement/Features/Tariff/types'
import { useAppDispatch } from 'Store'
import type { ProductTariffError } from 'Util/ApiClient'
import { UnsavedChangesProvider } from 'Util/Context/unsavedChangesContext'
import {
  DRAWER_TAB_PARAM_KEY,
  IS_TARIFF_EDIT_ALLOWED,
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT
} from 'Util/constants'
import useListFunctions from 'Util/hooks/useListFunctions'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import SettingsIcon from '@mui/icons-material/Settings'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Tab } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

function TariffCatalogProductListPage() {
  const { id: tariffId, catalogId } = useParams()
  const tariffRootUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useAppDispatch()

  const { singleTariff, isSingleTariffLoading } = useSelectedTariff(tariffId || '')
  const { data: singleCatalog, isFetching: isSingleCatalogFetching } = useGetSingleCatalogQuery(
    catalogId ?? '',
    {
      skip: !catalogId
    }
  )

  const {
    tariffCatalogProducts,
    tariffCatalogProductsLoading,
    searchQueryParams,
    setSearchQueryParams,
    total_results,
    selectedProductId,
    setSelectedProductId,
    fetchTariffCatalogProductsWithParams,
    resetQueryParams
  } = useProductsWithParams({
    inTariff: tariffId,
    inCatalog: catalogId
  })

  // Reset queryParams when tariffId or catalogId is changed (from the sidebar menu)
  useEffect(() => {
    if (
      (singleTariff && String(singleTariff.id) !== String(tariffId)) ||
      (singleCatalog && String(singleCatalog.id) !== String(catalogId))
    ) {
      resetQueryParams()
    }
  }, [tariffId, catalogId, singleTariff, singleCatalog])

  const selectedCatalogQueryParam = searchParams.get(SELECTED_CATALOG_PARAMETER_KEY) ?? undefined

  const [isCatalogDrawerOpen, setIsCatalogDrawerOpen] = useState(!!selectedCatalogQueryParam)
  const [isSelectedListItemDrawerOpen, setIsSelectedListItemDrawerOpen] = useState(
    !!selectedProductId
  )

  const { searchText, handleChangePage, handleRequestSort, handleSearchTextChange, handleSearch } =
    useListFunctions({
      searchQueryParams,
      setSearchQueryParams
    })

  const { tabFromParams, handleTabChange } = useProductListTabs({
    setSearchQueryParams
  })

  const {
    data: customerGroupParameterGroup = null,
    isLoading: isCustomerGroupParameterGroupLoading
  } = useGetCustomerGroupParameterGroupQuery()

  const { productGroupOptions, isProductGroupParameterGroupLoading } = useProductGroupOptions()

  const handleSelectedListItemDrawerClose = () => {
    setSelectedProductId('')
    setIsSelectedListItemDrawerOpen(false)
  }

  const handleCatalogDrawerOpen = (drawerTab: TariffCatalogTabType) => {
    searchParams.set(SELECTED_CATALOG_PARAMETER_KEY, catalogId as string)
    searchParams.set(DRAWER_TAB_PARAM_KEY, drawerTab)
    setSearchParams(searchParams)
    setIsCatalogDrawerOpen(true)
  }

  const handleCatalogDrawerClose = () => {
    searchParams.delete(SELECTED_CATALOG_PARAMETER_KEY)
    searchParams.delete(DRAWER_TAB_PARAM_KEY)
    setSearchParams(searchParams)
    setIsCatalogDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedProductId(id)
    setIsSelectedListItemDrawerOpen(true)
  }
  const handleProductsEditSaveSuccess = () => {
    fetchTariffCatalogProductsWithParams()
  }

  const { deleteProduct, deleteLoading: deleteProductLoading } = useSingleProduct(undefined)
  const handleProductDelete = (productId: string) => {
    deleteProduct(productId, { mode: ProductDeleteModes.CASCADE })
      .then(unwrapResult)
      .then(() => {
        setSelectedProductId('')
        setIsSelectedListItemDrawerOpen(false)
        fetchTariffCatalogProductsWithParams()
        // Invalidate catalog cache to update sidebar product counts
        // TODO: Remove when products use RTK Query and automatically invalidate the cache
        dispatch(catalogApi.util.invalidateTags(['Catalog']))
        SnackbarUtils.success(t`Product successfully deleted.`)
      })
      .catch(({ message }: ProductTariffError) => {
        if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error deleting the product.`)
        }
      })
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`,
      link: tariffRootUrl
    },
    {
      title: singleTariff ? singleTariff.description : '',
      link: `${tariffRootUrl}/${tariffId}/details`
    },
    {
      title: t`Tariff sales channels`
    },
    {
      title: singleCatalog ? singleCatalog.name : ''
    },
    {
      title: t`Sales channel products`
    }
  ]

  const isLoading =
    isSingleCatalogFetching ||
    tariffCatalogProductsLoading === 'pending' ||
    isSingleTariffLoading ||
    isCustomerGroupParameterGroupLoading ||
    deleteProductLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Sales channel products`}</PageTitle>
      </Box>
      <ListActions>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {IS_TARIFF_EDIT_ALLOWED && (
            <CustomButton
              onClick={() => handleCatalogDrawerOpen(TariffCatalogTabType.PRODUCTS)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Add products`}
            </CustomButton>
          )}
          <CustomButton
            onClick={() => handleCatalogDrawerOpen(TariffCatalogTabType.DETAILS)}
            size="small"
            startIcon={<SettingsIcon />}
            variant="outlined"
          >
            {t`Sales channel's details`}
          </CustomButton>
        </Box>
        <ListSearch
          searchText={searchText}
          handleSearchTextChange={handleSearchTextChange}
          handleSearch={handleSearch}
          placeholder={t`Search by name or code`}
        />
      </ListActions>
      <TabContext value={tabFromParams}>
        <WithLoader loading={isProductGroupParameterGroupLoading} rows={1} height={33}>
          <TabList onChange={handleTabChange} aria-label={t`Product group tab list`}>
            {productGroupOptions?.map(({ label, value }) => (
              <Tab key={value} label={label} value={value} />
            ))}
          </TabList>
        </WithLoader>
        <Box pt={1}>
          {productGroupOptions?.map(({ value }) => (
            <TabPanel sx={{ p: 0 }} key={value} value={value}>
              <ProductList
                rows={tariffCatalogProducts}
                isLoading={isLoading}
                params={searchQueryParams}
                handleRequestSort={handleRequestSort}
                handleChangePage={handleChangePage}
                count={total_results}
                page={
                  searchQueryParams.start_index
                    ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                    : 0
                }
                customerGroupsParameter={customerGroupParameterGroup}
                allowSelect={false}
                selectedProductId={selectedProductId}
                onRowClick={handleRowClick}
              />
            </TabPanel>
          ))}
        </Box>
      </TabContext>
      {/* Product details drawer */}
      <UnsavedChangesProvider>
        <TariffCatalogProductDrawer
          open={isSelectedListItemDrawerOpen}
          handleClose={handleSelectedListItemDrawerClose}
          handleProductDelete={handleProductDelete}
        />
      </UnsavedChangesProvider>
      {/* Catalog details drawer */}
      <WithLoader loading={isSingleTariffLoading}>
        {singleTariff && (
          <TariffCatalogDrawer
            open={isCatalogDrawerOpen}
            handleClose={handleCatalogDrawerClose}
            tariff={singleTariff}
            selectedCatalogId={catalogId}
            handleProductsEditSaveSuccess={handleProductsEditSaveSuccess}
          />
        )}
      </WithLoader>
    </Box>
  )
}

export default TariffCatalogProductListPage
